<template>
  <v-container fluid>
    <v-row class="ml-1">
      <global-stallholder-single-select
        @stallholder-changed="handleStallholderChange"
      />
    </v-row>
    <v-row>
      <v-col cols="12">
        <!-- Stalls Table -->
        <tables-event-management-table
          :items="displayStalls"
          :icon="tableIcon"
          :title="tableTitle"
          :buttons="tableButtons"
          :headers="tableHeaders"
          :no-data-message="noDataMessage"
          :show-actions="canShowStallActions"
          :can-manage-item="canEditStall"
          @edit="editItem"
          @delete="deleteItem"
          @addStall="handleAddStall"
        />

        <!-- Delete Confirmation Dialog -->
        <dialogs-delete-confirmation-dialog
          v-model="dialogDelete"
          title="Are you sure you want to delete this Stall?"
          @confirm="deleteItemConfirm"
          @cancel="closeDelete"
        />
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import "izitoast/dist/css/iziToast.min.css";
import iziToast from "izitoast/dist/js/iziToast.min.js";
import { mapState, mapMutations, mapActions } from "vuex";
import { hasWrite } from "@/utils/permissions";

export default {
  name: "Stalls",
  data: () => ({
    dialogDelete: false,

    // Table configuration
    tableIcon: "mdi-store",
    tableTitle: "Stalls",
    tableHeaders: [
      { text: "Stall Name", value: "StallName" },
      { text: "Order Numbers", value: "OrderNumbers" },
      { text: "ABN", value: "Abn" },
      { text: "Card Surcharge (%)", value: "CardSurchargePercentage" },
      { text: "Payment Profile", value: "PaymentProfile" },
      { text: "Printing Profile", value: "PrintingProfile" },
      { text: "Standard Drink Limit", value: "StandardDrinkLimit" },
      { text: "Drink Limit Profile", value: "StandardDrinkLimitEnforce" },
      { text: "Actions", value: "actions", sortable: false },
    ],
    noDataMessage: "No stalls for this stallholder",

    editedIndex: -1,

    editedItem: {
      StallHolderName: "",
      Event: "",
      Client: "",
    },
    defaultItem: {
      StallHolderName: "",
      Event: "",
      Client: "",
    },
  }),

  computed: {
    ...mapState("admin", [
      "currentEvent",
      "currentStallHolders",
      "currentStallholder",
      "currentStall",
      "currentStalls", // Add the currentStalls from store
    ]),
    ...mapState("filter", [
      "globalEventFilter",
      "IsAdmin",
      "userEventPermissions",
    ]),

    eventPermission() {
      return this.globalEventFilter?.Permission || "None";
    },

    stallholderPermission() {
      return this.currentStallholder?.Permission || "None";
    },

    /** Event or stallholder Write — can create stalls and manage all stalls under SH. */
    canCreateStalls() {
      return (
        this.IsAdmin ||
        hasWrite(this.eventPermission) ||
        hasWrite(this.stallholderPermission)
      );
    },

    canShowStallActions() {
      return (
        this.canCreateStalls ||
        this.displayStalls.some((stall) => this.canEditStall(stall))
      );
    },

    tableButtons() {
      if (!this.canCreateStalls) return [];
      return [
        {
          name: "Add Stall",
          icon: "mdi-plus",
          function: "addStall",
        },
      ];
    },

    // Computed property for current stalls that automatically updates when store changes
    displayStalls() {
      // If we have a current stallholder selected, always get the most up-to-date data
      if (this.currentStallholder && this.currentStallholder.StallholderId) {
        // First try to get fresh data from currentStallHolders (most up-to-date)
        if (this.currentStallHolders && this.currentStallHolders.length > 0) {
          const freshStallholder = this.currentStallHolders.find(
            (sh) => sh.StallholderId === this.currentStallholder.StallholderId
          );
          if (freshStallholder?.Stalls) {
            return freshStallholder.Stalls;
          }
        }

        // Fallback to currentStallholder.Stalls
        if (this.currentStallholder.Stalls) {
          return this.currentStallholder.Stalls;
        }

        // Fallback to store's currentStalls if it matches the current stallholder
        if (this.currentStalls && this.currentStalls.length > 0) {
          return this.currentStalls;
        }
      }

      // If no stallholder selected, use store's currentStalls
      if (this.currentStalls && this.currentStalls.length > 0) {
        return this.currentStalls;
      }

      return [];
    },
  },

  watch: {
    dialogDelete(val) {
      if (!val) {
        this.closeDelete();
      }
    },
  },

  methods: {
    ...mapMutations("admin", ["setCurrentStallholder", "setCurrentStall"]),
    ...mapActions("admin", ["createEvent", "getEventMetadata"]),

    resolveStallPermission(stall) {
      if (!stall) return "None";

      const stallId = stall.StallId;
      const shId =
        stall.StallholderId || this.currentStallholder?.StallholderId;
      const eventId =
        stall.EventId ||
        this.globalEventFilter?.EventId ||
        this.currentEvent?.EventId;

      // User ACL only — never trust metadata stall.Permission (often Write).
      if (!eventId || !shId || !stallId || !this.userEventPermissions?.length) {
        return "None";
      }
      const userEvent = this.userEventPermissions.find(
        (e) => e.EventId === eventId
      );
      const userSh = userEvent?.Stallholders?.find(
        (sh) => sh.StallholderId === shId
      );
      const userStall = userSh?.Stalls?.find((s) => s.StallId === stallId);
      return userStall?.Permission || "None";
    },

    canEditStall(stall) {
      return (
        this.canCreateStalls || hasWrite(this.resolveStallPermission(stall))
      );
    },

    refreshStallsData() {
      // This method is no longer needed as displayStalls is now a computed property
      // that automatically updates when the store changes
      console.log(
        "Stalls data automatically refreshed:",
        this.displayStalls.length,
        "stalls"
      );
    },
    handleStallholderChange(stallholder) {
      if (stallholder) {
        this.setCurrentStallholder(stallholder);
        console.log("displayStalls", this.displayStalls);
      }
    },
    editItem(item) {
      if (!this.canEditStall(item)) return;
      this.setCurrentStall(item);
      this.$router.push({
        name: "Stall Configuration",
        params: {
          id: item.StallId,
          stallData: item,
          stallholderData: this.currentStallholder,
        },
      });
    },

    deleteItem(item) {
      if (!this.canEditStall(item)) return;
      this.editedIndex = this.displayStalls.indexOf(item);
      this.editedItem = { ...item };
      this.dialogDelete = true;
    },

    async deleteItemConfirm() {
      try {
        if (this.editedItem.StallId) {
          const properties = {
            ...this.editedItem,
            EventId:
              this.editedItem.EventId ||
              this.globalEventFilter?.EventId ||
              this.currentEvent?.EventId,
            StallholderId:
              this.editedItem.StallholderId ||
              this.currentStallholder?.StallholderId,
          };
          const eventItem = [
            {
              Type: "Stall",
              Action: "delete",
              Properties: properties,
            },
          ];

          await this.createEvent({ event_items: eventItem });

          // Refresh the admin state to get updated data from server
          if (this.currentEvent?.EventId) {
            await this.getEventMetadata(this.currentEvent.EventId);
          }

          // Now refresh the local stalls data with the updated admin state
          this.refreshStallsData();

          iziToast.success({
            title: "Stall",
            message: "Successfully deleted record!",
            position: "topRight",
          });
        }
      } catch (error) {
        console.error("Delete error:", error);
        iziToast.warning({
          title: "Stall",
          message: "Failed to delete record!",
          position: "topRight",
        });
      }

      this.closeDelete();
    },

    close() {
      this.$nextTick(() => {
        this.editedItem = { ...this.defaultItem };
        this.editedIndex = -1;
      });
    },

    closeDelete() {
      this.dialogDelete = false;
      this.$nextTick(() => {
        this.editedItem = { ...this.defaultItem };
        this.editedIndex = -1;
      });
    },

    handleAddStall() {
      if (!this.canCreateStalls) return;
      if (!this.currentStallholder && this.currentStallHolders.length > 0) {
        this.setCurrentStallholder(this.currentStallHolders[0]);
      }

      this.$router.push({
        name: "Stall Configuration",
        params: { id: 0 },
      });
    },
  },
};
</script>
<style scoped>
h1 {
  color: #a09c94;
  font-family: sans-serif;
}
</style>
